import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { HttpClient } from '@angular/common/http';
import { BehaviorSubject, Observable, Subject } from 'rxjs';
import { map } from 'rxjs/operators';

import { environment } from 'src/environments/environments';
import { User } from './user';

@Injectable({ providedIn: 'root' })
export class UserService {
  private userSubject: BehaviorSubject<User | null>;
  public user: Observable<User | null>;
  private subject = new BehaviorSubject<string>('Initial Value');
  private userType: string = '';
  current = this.subject.asObservable();

  private arrayData = new BehaviorSubject([]);
  currentArray = this.arrayData.asObservable();

  constructor(
    private router: Router,
    private http: HttpClient,
  ) {
    this.userSubject = new BehaviorSubject(
      JSON.parse(localStorage.getItem('user')!),
    );
    this.user = this.userSubject.asObservable();
  }

  sendData(data: any) {
    this.subject.next(data);
  }
  public get userValue() {
    return this.userSubject.value;
  }
  getData() {
    return this.current;
  }

  sendArrayData(data: any) {
    this.arrayData.next(data);
  }

  setUserType(type: string) {
    this.userType = type;
    localStorage.setItem('userType', type);
  }

  getUserType(): string {
    return this.userType || localStorage.getItem('userType') || '';
  }

  clearUserType() {
    this.userType = '';
    localStorage.removeItem('userType');
  }

  // Customer
  getCustomer(page: any, status: any, search: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/list?page=` +
          page +
          '&status=' +
          status +
          '&search=' +
          search,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }

  getRoomateList(page: any, status: any, search: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/guest/list?page=` +
          page +
          '&status=' +
          status +
          '&search=' +
          search,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }

  // Builds the add/update customer request body. Per the Ejari API contract:
  // no file attached -> plain JSON object (vehicles/cheques as real arrays);
  // file attached -> FormData (multipart), with vehicles/cheques/deleteChequesIds
  // JSON.stringify'd into single form fields since multipart can't carry arrays.
  private buildCustomerBody(fields: Record<string, any>, ejariFile?: File | null, profileImageFile?: File | null): any {
    if (!ejariFile && !profileImageFile) {
      const body: any = {};
      Object.keys(fields).forEach((key) => {
        if (fields[key] !== undefined) body[key] = fields[key];
      });
      return body;
    }

    const formData = new FormData();
    Object.keys(fields).forEach((key) => {
      const value = fields[key];
      if (value === undefined) return;
      if (key === 'vehicles' || key === 'cheques' || key === 'deleteChequesIds') {
        formData.append(key, JSON.stringify(value));
      } else {
        formData.append(key, value);
      }
    });
    if (ejariFile) formData.append('ejari', ejariFile, ejariFile.name);
    if (profileImageFile) formData.append('profileImage', profileImageFile, profileImageFile.name);
    return formData;
  }

  createCustomer(
    name: any,
    address: any,
    email: any,
    contactNo: any,
    countryCode: any,
    roomNo: any,
    numberOfGuests: any,
    checkOutDate: any,
    breakfastOpted: any,
    buildingId?: any,
    unitId?: any,
    dateOfBirth?: any,
    moveInDate?: any,
    vehicles?: any,
    floorNumber?: any,
    chequeData?: any,
    ejariFile?: File | null,
    profileImageFile?: File | null,
  ) {
    const fields: Record<string, any> = {
      name: name,
      address: address,
      email: email,
      contactNo: contactNo,
      countryCode: countryCode,
      roomNo: roomNo,
      numberOfGuests: numberOfGuests,
      checkOutDate: checkOutDate,
      breakfastOpted: breakfastOpted,
    };
    if (buildingId) fields['buildingId'] = buildingId;
    if (unitId) fields['unitId'] = unitId;
    if (dateOfBirth) fields['dateOfBirth'] = dateOfBirth;
    if (moveInDate) fields['checkInDate'] = moveInDate;
    if (vehicles) fields['vehicles'] = vehicles;
    if (floorNumber) fields['floorNumber'] = floorNumber;
    if (chequeData?.cheques?.length) { fields['cheques'] = chequeData.cheques; fields['remindBefore'] = chequeData.remindBefore; }
    const formData = this.buildCustomerBody(fields, ejariFile, profileImageFile);
    return this.http
      .post<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/add`,
        formData,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }

  updateCustomer(
    id: any,
    name: any,
    address: any,
    email: any,
    contactNo: any,
    countryCode: any,
    roomNo: any,
    numberOfGuests: any,
    checkOutDate: any,
    breakfastOpted: any,
    buildingId?: any,
    unitId?: any,
    dateOfBirth?: any,
    moveInDate?: any,
    vehicles?: any,
    floorNumber?: any,
    chequeData?: any,
    ejariFile?: File | null,
    profileImageFile?: File | null,
    removeProfileImage?: boolean,
  ) {
    const fields: Record<string, any> = {
      name: name,
      address: address,
      email: email,
      contactNo: contactNo,
      countryCode: countryCode,
      roomNo: roomNo,
      numberOfGuests: numberOfGuests,
      checkOutDate: checkOutDate,
      breakfastOpted: breakfastOpted,
    };
    if (buildingId) fields['buildingId'] = buildingId;
    if (unitId) fields['unitId'] = unitId;
    if (dateOfBirth) fields['dateOfBirth'] = dateOfBirth;
    if (moveInDate) fields['checkInDate'] = moveInDate;
    if (vehicles) fields['vehicles'] = vehicles;
    if (floorNumber) fields['floorNumber'] = floorNumber;
    if (chequeData?.cheques?.length) {
      fields['cheques'] = chequeData.cheques;
      fields['remindBefore'] = chequeData.remindBefore;
      if (chequeData.deleteChequesIds?.length) {
        fields['deleteChequesIds'] = chequeData.deleteChequesIds;
      }
    }
    // Removing the photo and uploading a new one are mutually exclusive from
    // the form's own UI (picking a new file clears the remove flag), but
    // guard the wire format too: never send both.
    if (removeProfileImage && !profileImageFile) fields['removeProfileImage'] = true;
    const formData = this.buildCustomerBody(fields, ejariFile, profileImageFile);
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/update/${id}`,
        formData,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }
  checkout(id: any) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/checkout/${id}`,
        null,
      )
      .pipe(map((user) => user));
  }
  regenerate(id: any) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/regen-pass/${id}`,
        null,
      )
      .pipe(map((user) => user));
  }
  getCustomerlistbyId(id: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/view/${id}`,
      )
      .pipe(map((user) => user));
  }

  deleteCustomer(id: any) {
    return this.http
      .delete<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/delete/` +
          id,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }

  blockCustomer(id: any, status: any) {
    let formData = {
      status: status,
    };
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/update-status/` +
          id,
        formData,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }

  updateCutomerpassword(id: any, newPassword: any, confirmPassword: any) {
    var formData = {
      newPassword: newPassword,
      confirmPassword: confirmPassword,
    };
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/update-password/` +
          id,
        formData,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }

  // Dashboard
  getDashboardCountList(startDate: any, endDate: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/dashboard/dashboardStates?startDate=` +
          startDate +
          '&endDate=' +
          endDate,
      )
      .pipe(map((user) => user));
  }

  getDashboardChartList(
    startDate: any,
    endDate: any,
    apartmentId: any,
    vendorId: any,
  ) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/dashboard/chart?startDate=` +
          startDate +
          '&endDate=' +
          endDate +
          '&apartmentId=' +
          apartmentId +
          '&vendorId=' +
          vendorId,
      )
      .pipe(map((user) => user));
  }

  getDashboardLaundryStates(vendorId: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/dashboard/laundryOrderStates?vendorId=` +
          vendorId,
      )
      .pipe(map((user) => user));
  }

  getDashboardRoomServiceStates() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/dashboard/roomSeviceOrderStates?`,
      )
      .pipe(map((user) => user));
  }

  getResidentialDashboardStats(startDate: string = '', endDate: string = '') {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/residential-dashboard/stats?startDate=${startDate}&endDate=${endDate}`,
      )
      .pipe(map((user) => user));
  }

  getResidentialDashboardChart(startDate: any, endDate: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/residential-dashboard/chart?startDate=` +
          startDate +
          '&endDate=' +
          endDate,
      )
      .pipe(map((user) => user));
  }

  // Residential dashboard (revamp)
  getResidentialDashboardOverview(startDate: string, endDate: string) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/residential-dashboard/overview?startDate=${startDate}&endDate=${endDate}`,
      )
      .pipe(map((user) => user));
  }

  getResidentialOccupancyTrend(startDate: string, endDate: string, granularity: string = 'month') {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/residential-dashboard/occupancy-trend?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`,
      )
      .pipe(map((user) => user));
  }

  getResidentialRevenueOverview(startDate: string, endDate: string, granularity: string = 'month') {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/residential-dashboard/revenue-overview?startDate=${startDate}&endDate=${endDate}&granularity=${granularity}`,
      )
      .pipe(map((user) => user));
  }

  getResidentialUnitMix() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/residential-dashboard/unit-mix`,
      )
      .pipe(map((user) => user));
  }

  getResidentialRecentActivity(page: number = 1) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/residential-dashboard/recent-activity?page=${page}`,
      )
      .pipe(map((user) => user));
  }

  getResidentialActiveResidents(page: number = 1) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/residential-dashboard/active-residents?page=${page}&limit=20`,
      )
      .pipe(map((user) => user));
  }

  getResidentialOrderStates(startDate: string = '', endDate: string = '') {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/dashboard/residentialOrderStates?startDate=${startDate}&endDate=${endDate}`,
      )
      .pipe(map((user) => user));
  }

  // dashbaord Laundry
  getLaundryDashboardCountList(startDate: any, endDate: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/laundry/dashboard/dashboardStates?startDate=` +
          startDate +
          '&endDate=' +
          endDate,
      )
      .pipe(map((user) => user));
  }

  getlaundryDashboardChartList(startDate: any, endDate: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/laundry/dashboard/chart?startDate=` +
          startDate +
          '&endDate=' +
          endDate,
      )
      .pipe(map((user) => user));
  }

  getLaundryDashboardStates() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/laundry/dashboard/laundryOrderStates?`,
      )
      .pipe(map((user) => user));
  }
  // food dashboard
  getFoodDashboardCountList(startDate: any, endDate: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/restaurant/dashboard/dashboardStates?startDate=` +
          startDate +
          '&endDate=' +
          endDate,
      )
      .pipe(map((user) => user));
  }

  getFoodDashboardChartList(startDate: any, endDate: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/restaurant/dashboard/chart?startDate=` +
          startDate +
          '&endDate=' +
          endDate,
      )
      .pipe(map((user) => user));
  }

  getFoodDashboardStates() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/restaurant/dashboard/laundryOrderStates`,
      )
      .pipe(map((user) => user));
  }
  getFoodDashboardStates1() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/dashboard/restaurantOrderStates`,
      )
      .pipe(map((user) => user));
  }

  // createOneSignal(user_id: any, player_id: any, user_type: any) {
  //     const formData = {
  //         user_id: user_id,
  //         player_id: player_id,
  //         user_type: user_type
  //     };
  //     return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/onesignal/add`, formData)
  //         .pipe(map(user => {
  //             return user;
  //         }));
  // }

  // deleteOneSignal(user_id: any, player_id: any, user_type: any) {
  //     const formData = new FormData();
  //     formData.append('user_id', user_id);
  //     formData.append('player_id', player_id);
  //     formData.append('user_type', user_type);
  //     return this.http.post<any>(`${environment.apiUrl}${environment.apiPrefix}/admin/onesignal/delete`, formData)
  //         .pipe(map(user => {
  //             return user;
  //         }));
  // }

  createFcm(user_id: any, player_id: any, user_type: any) {
    const formData = {
      platform: 'WEB',
      token: player_id,
      user_id: user_id,
      user_type: user_type,
    };
    return this.http
      .post<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/save-fcm-token`,
        formData,
      )
      .pipe(map((res) => res));
  }

  deleteFcm(token: string, user_id?: string, user_type?: string) {
    const body = {
      token,
      user_id: user_id || localStorage.getItem('_restId'),
      user_type: user_type || localStorage.getItem('type'),
    };
    return this.http
      .delete<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/delete-fcm-token`,
        { body },
      )
      .pipe(map((res) => res));
  }

  getAdminNotification(id: any, page: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/notification/list?page=` +
          page,
      )
      .pipe(map((user) => user));
  }

  sendNotification(
    message: any,
    heading: any,
    image: any,
    apartmentId: any,
    targetType?: any,
    roomNumbers?: any,
    buildingIds?: string[],
    unitIds?: string[],
  ) {
    const formData = new FormData();
    formData.append('message', message);
    formData.append('heading', heading);
    formData.append('targetType', targetType);
    formData.append('roomNos', JSON.stringify(roomNumbers));

    if (buildingIds?.length) {
      formData.append('buildingIds', JSON.stringify(buildingIds));
    }
    if (unitIds?.length) {
      formData.append('unitIds', JSON.stringify(unitIds));
    }

    if (image) {
      formData.append('image', image, image.name);
    }
    formData.append('apartmentId', apartmentId);

    return this.http
      .post<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/notification/send`,
        formData,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }

  getCommunityNotices(page: any, search: any, isActive: any = '', category: string = '', priority: string = '', status: string = '') {
    let url = `${environment.apiUrl}${environment.apiPrefix}/apartment/community-notice/list?page=${page}&search=${search}&isActive=${isActive}`;
    if (category) url += `&category=${category}`;
    if (priority) url += `&priority=${priority}`;
    if (status) url += `&status=${status}`;
    return this.http.get<any>(url).pipe(map((user) => user));
  }


  viewCommunityNotice(id: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/community-notice/view/${id}`,
      )
      .pipe(map((user) => user));
  }
  
  createCommunityNotice(payload: FormData) {
    return this.http
      .post<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/community-notice/create`,
        payload,
      )
      .pipe(map((user) => user));
  }

  updateCommunityNotice(id: any, payload: FormData) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/community-notice/update/${id}`,
        payload,
      )
      .pipe(map((user) => user));
  }

  toggleCommunityNoticeStatus(id: any) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/community-notice/toggle-active/${id}`,
        {},
      )
      .pipe(map((user) => user));
  }

  getApartmentStatus() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/status-view`,
      )
      .pipe(map((user) => user));
  }
  getLaundryStatus() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/laundry/status-view`,
      )
      .pipe(map((user) => user));
  }
  getRestaurantStatus() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/restaurant/status-view`,
      )
      .pipe(map((user) => user));
  }

  regenerateguestpassword(id: any) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/guest/regen-pass/${id}`,
        null,
      )
      .pipe(map((user) => user));
  }

  getGuestVehicles(guestUserId: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/guest/vehicle/list/${guestUserId}`,
      )
      .pipe(map((user) => user));
  }

  addGuestVehicle(guestUserId: any, body: any) {
    return this.http
      .post<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/guest/vehicle/add/${guestUserId}`,
        body,
      )
      .pipe(map((user) => user));
  }

  updateGuestVehicle(vehicleId: any, body: any) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/guest/vehicle/update/${vehicleId}`,
        body,
      )
      .pipe(map((user) => user));
  }

  updateChequeStatus(chequeId: any, status: number) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/cheque-status/${chequeId}`,
        { status },
      )
      .pipe(map((user) => user));
  }

  deleteGuestVehicle(vehicleId: any) {
    return this.http
      .delete<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/guest/vehicle/delete/${vehicleId}`,
      )
      .pipe(map((user) => user));
  }

  downloadCustomerSampleExcel() {
    return this.http.get(
      `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/excel/sample-download`,
      { responseType: 'blob' }
    );
  }

  bulkUploadCustomerExcel(file: File) {
    const formData = new FormData();
    formData.append('file', file);
    return this.http.post<any>(
      `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/excel/bulk-upload`,
      formData
    ).pipe(map(res => res));
  }

  apartmentDropdown() {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/apartment/dropdown`,
      )
      .pipe(map((user) => user));
  }

  getApartmentList(page: any, search: any, status: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/apartment/list?search=` +
          search +
          '&page=' +
          page +
          '&status=' +
          status,
      )
      .pipe(map((user) => user));
  }

  paidStatusUpdate(id: any, useWallet: boolean) {
    var formData = {
      parentId: id,
      useWallet: useWallet,
    };
    return this.http
      .patch<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/billToDesk/paidStatusUpdate`,
        formData,
      )
      .pipe(
        map((user) => {
          return user;
        }),
      );
  }

  //popup notifications
  getNotifications(
    type:
      | 'ROOM_SERVICE'
      | 'RESTAURANT'
      | 'LAUNDRY'
      | 'RESIDENTIAL_ROOM_SERVICE'
      | 'CLUB',
    page = 1,
  ) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/order-notifications/list` +
          `?type=${type}&page=${page}`,
      )
      .pipe(map((res) => res));
  }

  /** Delete ALL notifications for a portal type */
  clearAllNotifications(
    type:
      | 'ROOM_SERVICE'
      | 'RESTAURANT'
      | 'LAUNDRY'
      | 'RESIDENTIAL_ROOM_SERVICE'
      | 'CLUB',
  ) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/order-notifications/clear-all?type=${type}`,
        {},
      )
      .pipe(map((res) => res));
  }

  /** Delete a SINGLE notification  */
  deleteNotification(id: string) {
    return this.http
      .put<any>(
        `${environment.apiUrl}${environment.apiPrefix}/admin/order-notifications/clear/${id}`,
        {},
      )
      .pipe(map((res) => res));
  }

  /*
   * Resident invitations (residential only) — /apartment/customer/invite.
   * Built from the API spec before the backend is live; response shapes are assumed:
   *   list → data: { docs: [{ _id, name, email, countryCode, contactNo, buildingName, unitNumber,
   *                  status, expiresAt, createdAt, userId?, correctionNote? }], page, totalPages, totalDocs, pagingCounter }
   *   view → data: { ...invite, building: { name }, unit: { unitNumber, floorNumber }, draft: { personal, household, vehicle[], documents[] } }
   *   link → data: { link, expiresAt }
   * status: sent | opened | in_progress | submitted | corrections_required (assumed) | approved | rejected | expired | revoked
   */
  getResidentInvites(page: any, search: any, status: any, startDate: any, endDate: any) {
    return this.http
      .get<any>(
        `${environment.apiUrl}${environment.apiPrefix}/apartment/customer/invite?page=` + page + '&limit=10&search=' + encodeURIComponent(search) +
        '&status=' + status + '&startDate=' + startDate + '&endDate=' + endDate,
      )
      .pipe(map((res) => res));
  }

  // body: { name, countryCode, contactNo, email?, buildingId, unitId, expectedMoveInDate?, sendVia: ['whatsapp','email'], linkExpiryHours: 24 | 72 | 168 }
  createResidentInvite(body: any) {
    return this.http
      .post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/customer/invite`, body)
      .pipe(map((res) => res));
  }

  getResidentInvite(id: any) {
    return this.http
      .get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/customer/invite/${id}`)
      .pipe(map((res) => res));
  }

  // Rotates the token: the previous link stops working
  getResidentInviteLink(id: any) {
    return this.http
      .get<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/customer/invite/${id}/link`)
      .pipe(map((res) => res));
  }

  // body: { sendVia, linkExpiryHours, correctionNote?, countryCode?, contactNo?, email?, buildingId?, unitId? } — correctionNote is assumed (not in the spec yet);
  // contact fields correct the mobile / email, buildingId + unitId move a disputed invite (confirmed: same keys as create)
  resendResidentInvite(id: any, body: any) {
    return this.http
      .post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/customer/invite/${id}/resend`, body)
      .pipe(map((res) => res));
  }

  approveResidentInvite(id: any) {
    return this.http
      .post<any>(`${environment.apiUrl}${environment.apiPrefix}/apartment/customer/invite/${id}/approve`, {})
      .pipe(map((res) => res));
  }
}
